/**
 * A small Markdown renderer for agent replies: paragraphs, headings, bullet and numbered lists,
 * fenced code, inline code, bold and pipe tables. It builds React elements only, so all text is
 * escaped by React; it never produces HTML strings. Links are shown as plain text.
 */
import React from 'react';

type Block =
  | { type: 'p'; lines: string[] }
  | { type: 'h'; level: number; text: string }
  | { type: 'ul' | 'ol'; items: string[] }
  | { type: 'code'; text: string }
  | { type: 'table'; head: string[]; rows: string[][] };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const FENCE = /^\s*```/;
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

export function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (FENCE.test(line)) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence (or end of text)
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ type: 'h', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && TABLE_RULE.test(lines[i + 1])) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(cells(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', head, rows });
      continue;
    }
    const listType = BULLET.test(line) ? 'ul' : NUMBERED.test(line) ? 'ol' : null;
    if (listType) {
      const pattern = listType === 'ul' ? BULLET : NUMBERED;
      const items: string[] = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push((pattern.exec(lines[i]) as RegExpExecArray)[1]);
        i += 1;
      }
      blocks.push({ type: listType, items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !FENCE.test(lines[i]) && !HEADING.test(lines[i])
      && !BULLET.test(lines[i]) && !NUMBERED.test(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'p', lines: para });
  }
  return blocks;
}

/** Inline code (`x`) and bold (**x**); everything else is text. */
export function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const pattern = /`([^`]+)`|\*\*([^*]+)\*\*/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    if (match[1] !== undefined) out.push(<code key={key}>{match[1]}</code>);
    else out.push(<strong key={key}>{match[2]}</strong>);
    key += 1;
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const Markdown: React.FC<{ text: string }> = ({ text }) => (
  <div className="agent-md">
    {parseBlocks(text).map((block, i) => {
      switch (block.type) {
        case 'code':
          return <pre key={i} className="agent-code"><code>{block.text}</code></pre>;
        case 'h': {
          const Tag = (block.level <= 2 ? 'h3' : 'h4') as 'h3' | 'h4';
          return <Tag key={i}>{renderInline(block.text)}</Tag>;
        }
        case 'ul':
          return <ul key={i}>{block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}</ul>;
        case 'ol':
          return <ol key={i}>{block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}</ol>;
        case 'table':
          return (
            <div key={i} className="agent-table-wrap">
              <table className="agent-table">
                <thead><tr>{block.head.map((c, j) => <th key={j}>{renderInline(c)}</th>)}</tr></thead>
                <tbody>
                  {block.rows.map((row, r) => (
                    <tr key={r}>{row.map((c, j) => <td key={j}>{renderInline(c)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        default:
          return (
            <p key={i}>
              {block.lines.map((l, j) => (
                <React.Fragment key={j}>{j > 0 && <br />}{renderInline(l)}</React.Fragment>
              ))}
            </p>
          );
      }
    })}
  </div>
);

export default Markdown;
